/**
 * QMP-FE-6 — STEP 7 "Approvals".
 *
 * LAYOUT AND COPY ARE THE MOCKUP'S (`mockup-qmp-v79-official.html`, `#w7`
 * lines 628-656 and `renderApr` 1371-1464); every visual value is read from
 * that file's CSS (`.endt`, `.chip`, `.sigline`, `.batch`, `.ackbox`,
 * `.gateline`, `.preview`, `.sheet`), none is guessed.
 *
 * THE OPENING IS WRITTEN FOR WHOEVER APPROVES (D14.9, v79 note "v79 — abertura
 * do passo 7"): this page is read by the person who has to sign, not only by
 * the company that published.
 *
 * NOTHING HERE DECIDES WHO MAY SIGN. The server serves `approvals[].can_decide`
 * (QMP-FE-7, from the same function its door raises from) and the controls
 * follow it and nothing else; the directory read only ADDRESSES (whose row is
 * whose, who may be named). A refusal still comes back as `NOT_QMP_SIGNATORY`
 * or `NOT_REQUIRED_APPROVER` and is shown where the act was attempted.
 *
 * QMP-FE-8a (D30, decision 140 A) — the company approves in ONE act what the
 * server says is waiting for it (`approval_act`), sent back as served; the
 * panels of that act are the v83 mockup's (`mockup-qmp-v83-official.html`,
 * `approvalPage` l. 2114-2130, `recordOwnDecision` l. 2247-2261). The lots of
 * packages with the same rules (old D11.9) are gone from the screen.
 *
 * QMP-FE-8g — THE PAGE AROUND THAT ACT is the v83 mockup's `approvalPage`
 * (l. 2086-2131): "Revision n · agreement per QWP" and its intro, the status
 * line "Approval due: … · by …" / "n companies asked", the table "Quality
 * Work Package · Approvals needed · Agreement · Details" with "View
 * agreement", the info line, the agreement panel of one QWP, and before any
 * publication "The QMP is still a draft" with "Go to Review & publish". The
 * act of QMP-FE-8a (`approval_act` -> `scope` + `participation_id`, the
 * objection per QWP, the 409, "Before you sign", one action at a time) is
 * untouched. Not drawn, because `GET /qmp` does not serve it: the "common /
 * own" summary of a PUBLISHED revision inside "your review" (a revision
 * freezes each package's resolved rules, never the common rule), so "your
 * review" keeps each QWP's published sheet; the revision a carried approval
 * came from (`last_record` carries none).
 *
 * The mockup's `.eyebar` (revision switches, "Seen by a company") is
 * demonstration scaffolding and is not drawn: the state is the server's and
 * the reader is whoever is signed in.
 */
import React from 'react';
import { LoadError, QButton, useTip } from './ItpRulesStep';
import { BeforeYouSign, Part, companyNames } from './PublishStep';
import {
  approveQmpPackage, approveQmpPackagesBatch, setQmpApprovalNominee,
} from '../../lib/api';
import { partyName } from '../../lib/qmpReview';
import {
  approvalSheet, statusChip, lastRecordLine, roleLine,
  dueLine, companiesAsked, askedLine, changeLabel, changedSentence, requestText,
  partyState, agreementCell, agreementBox, APPROVALS_COPY, pageTitle,
  decisionBody, describeApprovalError, errorPlace,
  canDecide, isSettled, decidableRow,
  readAct, actOpen, actRows, actBody, actProblem, plural, decisionContext, signingKey,
  outcomeUnknown, ACT_PROBLEM, ACT_OUTCOME,
  formatDay, lastApprovalAt, REASON_MAX, objectionReason,
  historyByRevision, historyLine, recordReason,
} from '../../lib/qmpApprovals';
import useQmpRevisionDiff from '../../hooks/useQmpRevisionDiff';
import useQmpPackageApprovals from '../../hooks/useQmpPackageApprovals';
import useProjectMembership from '../../hooks/useProjectMembership';

const CARD = 'bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] min-w-0';

// Mockup `.preview` (v79:97-102) and its title (`.preview .t`).
export const BOX_T = 'text-[10.5px] tracking-[.1em] uppercase text-[#5b6b85] font-bold';
const PREVIEW = 'bg-[#0b1322] border border-[#16223a] rounded-[9px] px-[16px] py-[14px] mt-[14px]';
const PREVIEW_LI = 'list-none text-[12.5px] text-[#94a3b8] mb-[6px] [&_b]:text-[#f4f7fc]';

// The two tooltips of the step (v79:653-654), VERBATIM.
export const TIPS7 = {
  appr: {
    title: 'Why these companies must approve',
    body: (
      <>
        These companies have a quality role in this package — Executing, Verification, Acceptance,
        ITP authoring or ITP endorsement. <b>A company approves once for the package, even if it has
        more than one role.</b>
      </>
    ),
  },
  sig: {
    title: 'Who will approve for each company?',
    body: (
      <>
        Select the person who should receive the approval request. <b>This does not give them
        approval authority.</b> Only a Quality Management Plan Signatory for that company can approve the QMP.
        If the selected person is not authorised, that company&apos;s Company Representative can
        authorise them, or a different person can be selected. <b>The nominated company can always change the nominee to
        one of its own Quality Management Plan Signatories.</b>
      </>
    ),
  },
};

// QMP-FE-8g — the chips of a company's line, in v83's `.cq-badge` colours
// (dark values of `#cqa-qmp-flow` l. 1248; `.cq-badge` l. 1277-1279, 1382).
const CHIP = 'inline-flex items-center gap-[6px] text-[12px] font-bold px-[9px] py-[4px] '
  + 'rounded-[5px] whitespace-nowrap';
export const CHIP_TONE = {
  ok: `${CHIP} text-[#78d4ae] bg-[#122d28]`,
  warn: `${CHIP} text-[#ffd28b] bg-[#332819]`,
  bad: `${CHIP} text-[#ffa9b2] bg-[#3a1c24]`,
  dim: `${CHIP} text-[#afbdd0] bg-[#162238]`,
  lock: `${CHIP} text-[#afbdd0] bg-[#162238]`,
};

// v83 `.cq-subhead` (l. 1393).
const SECLBL = 'mt-[16px] mb-[8px] text-[13px] tracking-[.04em] text-[#afbdd0] font-medium';
// v83 `.cq-small` (l. 1289).
const HS = 'text-[12px] text-[#afbdd0]';

/** The chain under a package name (mockup v79:1395). */
export function chainLine(row) {
  const parts = [partyName(row, 'execution')];
  if (row && row.verification) parts.push(partyName(row, 'verification'));
  if (row && row.acceptance) parts.push(partyName(row, 'acceptance'));
  return parts.join(' → ');
}

// The sheet of a package as the approver reads it: the rule being approved,
// and where the contract said a section moved, the previous rule struck
// through beside it (`.sheet .chg`, `s.old` — v79:288).
function DiffSheet({ rows, testId }) {
  return (
    <div
      className="grid grid-cols-[140px_minmax(0,1fr)] gap-x-[16px] gap-y-[7px] text-[12.5px] text-[#94a3b8] mt-[6px] mb-[12px]"
      data-testid={testId}
    >
      {rows.map((r) => (
        <React.Fragment key={r.label}>
          <div
            className={`text-[10.5px] tracking-[.08em] uppercase pt-[2px] ${
              r.was ? 'text-[#f97316] bg-[rgba(249,115,22,.07)]' : 'text-[#5b6b85]'
            }`}
          >
            {r.label}
          </div>
          <div
            className={r.was ? 'bg-[rgba(249,115,22,.07)]' : undefined}
            data-testid={`${testId}-${r.label}`}
          >
            {r.was && (
              <s className="text-[#5b6b85] line-through mr-[4px] block" data-testid={`${testId}-${r.label}-was`}>
                {r.was.map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line.map((p, j) => <Part key={j} p={p} />)}
                  </React.Fragment>
                ))}
              </s>
            )}
            {r.lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line.map((p, j) => <Part key={j} p={p} />)}
              </React.Fragment>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/** "The rules being approved" / "What changed since Revision N" / "Nothing
 *  changed in this package" — which of the three is the CONTRACT's answer
 *  (`previous_rules_hash`, `sections_changed`), not a guess. */
function RulesBlock({
  row, entry, revision, diff, previousPackages, names, index, idp = 'qa',
}) {
  const rules = (entry && entry.current && entry.current.rules) || null;
  const d = (diff && diff[row.qwp_id]) || {};
  const first = !d.previous_rules_hash;
  const changed = (d.sections_changed || []).length > 0;
  const previous = (previousPackages && previousPackages[row.qwp_id]
    && previousPackages[row.qwp_id].rules) || null;

  if (!first && !changed) {
    return (
      <div className="text-[12.5px] text-[#94a3b8]" data-testid={`${idp}-unchanged-${index}`}>
        <b className="text-[#f4f7fc]">Nothing changed in this package.</b>
        {` The approvals from Revision ${revision - 1} remain valid, so no new approval is required.`}
      </div>
    );
  }
  const sheet = approvalSheet(row, {
    rules,
    previousRules: previous,
    sectionsChanged: d.sections_changed,
    previousRulesHash: d.previous_rules_hash,
    companyNames: names,
  });
  return (
    <>
      <div className={SECLBL} data-testid={`${idp}-ruleslbl-${index}`}>
        {first ? 'The rules being approved' : `What changed since Revision ${revision - 1}`}
      </div>
      <DiffSheet rows={sheet} testId={`${idp}-sheet-${index}`} />
      {!first && (
        <div className={`${HS} mt-[6px]`} data-testid={`${idp}-diffnote-${index}`}>
          Rules without a strike-through did not change. Existing signed records are unaffected, and
          ITCs already created keep the rules they were created under.
        </div>
      )}
    </>
  );
}

/** The badge: the server's count, never a recount here (v79:1457-1458). */
export function approvalsBadge(summary) {
  const outstanding = summary ? Number(summary.outstanding || 0) : 0;
  return outstanding === 0
    ? { text: 'ALL APPROVED', live: true }
    : { text: 'AWAITING APPROVAL', live: false };
}

/** The headline of "the approval record" once nothing is outstanding.
 *  The publication date is the revision's own (`declared_at`); the date every
 *  company had answered by is DERIVED from the rows already on the page and is
 *  simply left out when no row carries one — the contract serves no such
 *  field, and a date nobody recorded is not printed. */
export function recordHeadline(revision, currentRevision, settledAt) {
  const published = formatDay((currentRevision || {}).declared_at, { withYear: true });
  const head = `Revision ${revision}${published ? ` · published ${published}` : ''}`;
  const when = formatDay(settledAt, { withYear: true });
  return `${head} · approved by every company${when ? ` on ${when}` : ''}.`;
}

/** Has anything been published at all? `approvals[]` is `[]` while
 *  `governance` is not "published", and `approvals_summary.revision` is null
 *  before the first revision — so this is the server saying "not yet". */
export const isPublished = (qmp) => Boolean(
  qmp && qmp.current_revision && qmp.approvals_summary && qmp.approvals_summary.revision,
);

// One company of the agreement panel — v83 `.cq-summary-line` (l. 1322-1325):
// a line between rules, the first without one.
const SIGLINE = 'flex flex-wrap gap-[10px] items-center py-[11px] border-t border-[#2b3c56] '
  + 'text-[13px] first:border-t-0';

const NOT_NOMINATED = 'Not nominated yet';

/** The person this company's request should go to. Mockup `select.control`
 *  200px (v79:1426). The list is the company's LIVE members, from the read
 *  every member already has; the door refuses anyone else (422 NOT_A_MEMBER).
 *
 *  NOMINATING ADDRESSES; IT DOES NOT GRANT AUTHORITY — the caption beside it
 *  says so, and whether an approval is accepted is decided by the door every
 *  single time, which never reads this choice. */
function NomineeSelect({
  row, people, onPick, busy, testId,
}) {
  const current = (row.nominee && row.nominee.source === 'nominated' && row.nominee.user_id) || '';
  return (
    <select
      className="w-[200px] bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px] disabled:opacity-60"
      value={current}
      disabled={busy}
      onChange={(e) => onPick(e.target.value)}
      data-testid={testId}
    >
      <option value="">{NOT_NOMINATED}</option>
      {people.map((m) => (
        <option key={m.id} value={m.id}>{m.name}</option>
      ))}
    </select>
  );
}

/** Where the request for this company goes (NL21-A10.3): the person named for
 *  it, or — when nobody was named — that company's Company Representatives.
 *  AN ADDRESS, NEVER AN AUTHORITY, and the sentence says so. */
function NomineeLine({ row, index, position }) {
  if (!row.nominee) {
    return (
      <span className={CHIP_TONE.warn} data-testid={`qa-nominee-${index}-${position}`}>
        {requestText(row)}
      </span>
    );
  }
  return (
    <span
      className="text-[12px] text-[#afbdd0] min-w-[130px]"
      data-testid={`qa-nominee-${index}-${position}`}
    >
      {requestText(row)}
    </span>
  );
}

// "Who approved for each company" (v79:1411-1419, 1424-1446) — one line per
// company the plan obliges, with the state the server serves and nothing the
// screen worked out. `overdue` IS A LABEL: nothing here approves by time.
function WhoApproved({
  entry, index, mine, children, nominate, openTip,
}) {
  const approvals = (entry && entry.approvals) || [];
  if (!approvals.length) return null;
  const settled = approvals.every((a) => a.status === 'approved' || a.status === 'not_affected');
  return (
    <div className="mt-[20px]" data-testid={`qa-who-${index}`}>
      <div className={SECLBL} data-testid={`qa-who-${index}-title`}>
        {settled ? 'Who approved for each company' : 'Who will approve for each company?'}
        {!settled && openTip && <QButton tipKey="sig" onOpen={openTip} />}
      </div>
      {approvals.map((row, j) => {
        const chip = statusChip(row);
        const last = lastRecordLine(row);
        const reason = objectionReason(row);
        const isMine = Boolean(mine) && row.participation_id === mine;
        return (
          <div className={SIGLINE} key={row.participation_id} data-testid={`qa-sig-${index}-${j}`}>
            <span className="min-w-[150px] font-medium">{row.name}</span>
            <span className="min-w-[150px] text-[#afbdd0] text-[12px]">{roleLine(row.roles)}</span>
            {nominate ? nominate(row, j, index) : (
              <NomineeLine row={row} index={index} position={j} />
            )}
            <span className={CHIP_TONE[chip.tone]} data-testid={`qa-sig-${index}-${j}-chip`}>
              {chip.text}
            </span>
            {isMine && (
              <span className={CHIP_TONE.lock} data-testid={`qa-sig-${index}-${j}-mine`}>
                You are named here
              </span>
            )}
            {last && (
              <span
                className="basis-full text-[12px] text-[#afbdd0]"
                data-testid={`qa-sig-${index}-${j}-last`}
              >
                {last.superseded
                  ? `${last.text} — that decision was taken against rules that have since changed, so this package is waiting again.`
                  : last.text}
              </span>
            )}
            {/* QMP-FE-7 — the objection's reason, as the server wrote it
                (v79:1439). Only on an objection that carries words: an
                approval never draws an empty field. */}
            {reason && (
              <span
                className="basis-full text-[13px] text-[#f1f5fb]"
                data-testid={`qa-sig-${index}-${j}-reason`}
              >
                <b className="font-bold">Reason:</b>
                {` ${reason}`}
              </span>
            )}
            {children && children(row, j, isMine)}
          </div>
        );
      })}
    </div>
  );
}

// Mockup `.btn` (v79:1438): the primary act, the ghost beside it, and the
// disabled opacity of `.btn[disabled]` (v79:178).
const BTN_PRIMARY = 'px-[14px] py-[7px] rounded-[9px] font-bold text-[12.5px] bg-[#f97316] '
  + 'text-[#180b02] disabled:opacity-45 disabled:cursor-not-allowed';
const BTN_GHOST = 'px-[12px] py-[7px] rounded-[9px] font-bold text-[12.5px] border '
  + 'border-[#1c2941] text-[#94a3b8] disabled:opacity-45 disabled:cursor-not-allowed';
const ERR = 'text-[12.5px] text-[#fca5a5] border border-[rgba(239,68,68,.45)] '
  + 'bg-[rgba(239,68,68,.08)] rounded-[9px] px-[12px] py-[9px] mt-[10px]';

/** The refusal the server gave, where it belongs — never a sentence of ours. */
function Refusal({ error, place, testId }) {
  if (!error || errorPlace(error.code) !== place) return null;
  return <div className={ERR} data-testid={testId}>{error.message}</div>;
}

// Object… — a reason is what the other companies read, so the screen asks for
// one before it offers to send; the door asks again (422 `REASON_REQUIRED`).
function ObjectForm({
  reason, onReason, onSend, onCancel, busy, testId,
}) {
  return (
    <div className="basis-full mt-[8px]" data-testid={`${testId}-form`}>
      <textarea
        className="w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px]"
        rows={3}
        maxLength={REASON_MAX}
        value={reason}
        placeholder="Say why you are objecting; the other companies read it."
        onChange={(e) => onReason(e.target.value)}
        data-testid={`${testId}-reason`}
      />
      <div className="flex items-center gap-[8px] mt-[8px]">
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={!reason.trim() || busy}
          onClick={onSend}
          data-testid={`${testId}-send`}
        >
          Send objection
        </button>
        <button type="button" className={BTN_GHOST} onClick={onCancel} data-testid={`${testId}-cancel`}>
          Cancel
        </button>
        <span className="text-[11.5px] text-[#5b6b85]" data-testid={`${testId}-count`}>
          {`${reason.length} / ${REASON_MAX}`}
        </span>
      </div>
    </div>
  );
}

// Mockup `.same` (v79:193-194): one line of the history.
const SAME = 'list-none text-[12.5px] text-[#94a3b8] mb-[5px] [&_b]:text-[#f4f7fc]';

/** QMP-FE-7 — "Approval history" of one package (v79:1420), from
 *  `GET .../packages/{qwp_id}/approvals` and ONLY what it serves: one line per
 *  record (company, roles, decision, person, date, one act over several),
 *  grouped under its revision, newest first, and an objection's reason under
 *  it. Nothing is summarised ("approved by all N", "carried forward"): the
 *  contract serves records, not those claims (DV-2).
 *  404 — outside the fence — and no records: the section is not drawn.
 *  503 or any other failure: the server's sentence and "Try again". */
function ApprovalHistory({
  projectId, qwpId, index, refreshKey,
}) {
  const {
    state, records, message, retry,
  } = useQmpPackageApprovals(projectId, qwpId, refreshKey);
  const testId = `qa-phist-${index}`;
  if (state === 'error') {
    return (
      <div className="mt-[20px]" data-testid={testId}>
        <div className={SECLBL}>Approval history</div>
        <div className="flex items-center gap-[10px] flex-wrap">
          <span className={ERR} data-testid={`${testId}-error`}>{message}</span>
          <button type="button" className={`${BTN_GHOST} mt-[10px]`} onClick={retry} data-testid={`${testId}-retry`}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (state !== 'ok') return null;
  const groups = historyByRevision(records);
  if (!groups.length) return null;
  return (
    <div className="mt-[20px]" data-testid={testId}>
      <div className={SECLBL}>Approval history</div>
      <ul>
        {groups.map((g) => (
          <React.Fragment key={g.revision}>
            <li className={SAME} data-testid={`${testId}-r${g.revision}`}>
              <b>{`Revision ${g.revision}`}</b>
            </li>
            {g.records.map((rec, k) => {
              const why = recordReason(rec);
              return (
                <li
                  className={`${SAME} pl-[12px]`}
                  key={`${rec.participation_id}-${rec.decided_at || k}`}
                  data-testid={`${testId}-r${g.revision}-${k}`}
                >
                  <span data-testid={`${testId}-r${g.revision}-${k}-line`}>{historyLine(rec)}</span>
                  {why && (
                    <span
                      className="block text-[12px] text-[#94a3b8]"
                      data-testid={`${testId}-r${g.revision}-${k}-reason`}
                    >
                      <b className="text-[#f4f7fc]">Reason:</b>
                      {` ${why}`}
                    </span>
                  )}
                </li>
              );
            })}
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
}

/** What `GET /qmp` served for a package's companies: when it moves (the page
 *  re-reads the plan after every decision), the history is read again. */
const historyKey = (approvals) => approvals.map((a) => [
  a.participation_id, a.status, (a.last_record && a.last_record.decided_at) || '',
].join(':')).join('|');

// QMP-FE-8a — mockup v83 (`docs/design/mockups/mockup-qmp-v83-official.html`),
// dark values of its tokens (`#cqa-qmp-flow`, l. 1248): `.cq-panel` (l. 1282),
// `h3` (1273, the base sheet's medium weight 219-225), `p` (1274), `strong`
// (228-232), `.cq-actions` (1291), `button.cq-button.cq-primary` (1292-1293,
// disabled 1295), `button.cq-quiet` (1294), `#cq-feedback:not(:empty)`
// (1302). Font: the page's Manrope (decision 139 A), not v83's Arial.
const V83_PANEL = 'bg-[#101a2b] border border-[#2b3c56] rounded-[10px] p-[18px] my-[16px] text-[14px] leading-[1.5] text-[#f1f5fb]';
const V83_H3 = 'text-[15px] font-medium mb-[5px]';
const V83_P = 'mb-[12px]';
const V83_PRIMARY = 'min-h-[44px] border border-[#ff7a12] rounded-[7px] px-[13px] py-[9px] bg-[#ff7a12] '
  + 'text-[#17202d] font-bold text-[14px] disabled:opacity-50 disabled:cursor-not-allowed';
const V83_QUIET = 'min-h-[44px] border border-transparent rounded-[7px] px-[4px] py-[9px] bg-transparent '
  + 'text-[#ff8b32] text-[14px] text-left disabled:opacity-50 disabled:cursor-not-allowed';
const V83_ACTIONS = 'flex items-center gap-[10px] flex-wrap mt-[16px]';
const V83_FEEDBACK = 'rounded-[6px] px-[12px] py-[10px] mb-[14px] text-[13px] bg-[#122d28] text-[#78d4ae]';

// QMP-FE-8g — the rest of v83 `approvalPage`, dark values of the same tokens:
// `h2` (l. 1272), `.cq-intro` (1281), `.cq-status-line` (1280), `.cq-info`
// (1298), `.cq-pending` / `.cq-success` (1299-1300), `.cq-small` (1289),
// `.cq-badge` (1277-1279, 1382), `.cq-table` (1313-1321) and its phone form
// (1330: one block per QWP, each cell headed by its `data-label`).
const V83_H2 = 'text-[20px] leading-[1.3] font-medium mb-[7px] text-[#f1f5fb]';
const V83_INTRO = 'text-[14px] leading-[1.5] text-[#afbdd0] max-w-[72ch] mb-[12px]';
const V83_STATUS = 'flex flex-wrap gap-y-[8px] gap-x-[20px] justify-between mt-[14px] mb-[24px] pb-[14px] '
  + 'border-b border-[#2b3c56] text-[12px] text-[#afbdd0]';
const V83_INFO = 'border-l-[3px] border-[#ff8b32] px-[13px] py-[10px] bg-[#32251e] my-[14px] text-[14px] '
  + 'leading-[1.5] text-[#f1f5fb]';
const V83_PENDING = 'border-l-[3px] border-[#ffd28b] px-[14px] py-[12px] bg-[#332819] my-[12px] text-[14px] '
  + 'leading-[1.5] text-[#f1f5fb]';
const V83_SUCCESS = 'border-l-[3px] border-[#78d4ae] px-[14px] py-[12px] bg-[#122d28] my-[12px] text-[14px] '
  + 'leading-[1.5] text-[#f1f5fb]';
const V83_SMALL = 'text-[12px] text-[#afbdd0]';
const V83_BADGE_BASE = 'inline-block px-[9px] py-[4px] rounded-[5px] text-[12px] font-bold';
const V83_BADGE = {
  good: `${V83_BADGE_BASE} text-[#78d4ae] bg-[#122d28]`,
  warn: `${V83_BADGE_BASE} text-[#ffd28b] bg-[#332819]`,
  bad: `${V83_BADGE_BASE} text-[#ffa9b2] bg-[#3a1c24]`,
};
const V83_TABLE = 'w-full border-collapse table-fixed text-[13px] max-[580px]:block';
const V83_TH = 'text-left text-[#afbdd0] text-[11px] font-bold px-[8px] py-[10px] border-b border-[#2b3c56] '
  + 'first:pl-0 last:pr-0';
const V83_TR = '[&:last-child>td]:border-b-0 max-[580px]:block max-[580px]:w-full max-[580px]:py-[12px] '
  + 'max-[580px]:border-b max-[580px]:border-[#2b3c56] max-[580px]:last:border-b-0';
const V83_TD = 'px-[8px] py-[11px] align-middle border-b border-[#2b3c56] first:pl-0 last:pr-0 '
  + 'max-[580px]:block max-[580px]:w-full max-[580px]:px-0 max-[580px]:py-[4px] max-[580px]:border-0';
const V83_TD_LABEL = "max-[580px]:before:content-[attr(data-label)_':_'] max-[580px]:before:text-[#afbdd0] "
  + 'max-[580px]:before:text-[12px]';
const V83_SOURCE = 'block text-[11px] text-[#afbdd0] mt-[3px] font-normal';
const V83_TABLE_BUTTON = 'min-h-[40px] max-[580px]:min-h-[44px] border border-[#2b3c56] rounded-[7px] p-[8px] '
  + 'bg-[#0b1423] text-[#f1f5fb] text-[12px] whitespace-normal';

/** Before the first publication (v83 l. 2087): nothing has been asked, and
 *  the way to step 5. */
function NothingPublished({ onGo }) {
  return (
    <>
      <h2 className={V83_H2} data-testid="qa-title">{APPROVALS_COPY.draftTitle}</h2>
      <div className={V83_PENDING} data-testid="qa-empty">
        <strong className="font-medium">{APPROVALS_COPY.draftHead}</strong>
        <br />
        {APPROVALS_COPY.draftBody}
      </div>
      <section className={V83_PANEL} data-testid="qa-next-step">
        <h3 className={V83_H3}>{APPROVALS_COPY.nextTitle}</h3>
        <p className={V83_P}>{APPROVALS_COPY.nextBody}</p>
        <button type="button" className={V83_PRIMARY} onClick={() => onGo(5)} data-testid="qa-go-publish">
          {APPROVALS_COPY.goPublish}
        </button>
      </section>
    </>
  );
}

/** One QWP of the table (v83 l. 2092-2098): its name and whether this
 *  revision changed it, each company that must approve with its state and
 *  where the request goes, the agreement, and "View agreement". */
function PackageRow({
  row, entry, index, revision, diff, open, onView,
}) {
  const approvals = (entry && entry.approvals) || [];
  const cell = agreementCell(entry, revision);
  const change = changeLabel(diff && diff[row.qwp_id], revision);
  return (
    <tr className={V83_TR} data-testid={`qa-row-${index}`}>
      <td className={`${V83_TD} font-bold`} data-testid={`qa-name-${index}`}>
        {row.name}
        {change && <span className={V83_SOURCE} data-testid={`qa-change-${index}`}>{change}</span>}
      </td>
      <td className={`${V83_TD} ${V83_TD_LABEL}`} data-label="Companies" data-testid={`qa-needed-${index}`}>
        {approvals.map((a, j) => {
          const s = partyState(a);
          return (
            <div className="my-[4px]" key={a.participation_id} data-testid={`qa-party-${index}-${j}`}>
              {a.name}
              {' '}
              <span className={V83_BADGE[s.tone]} data-testid={`qa-party-${index}-${j}-badge`}>{s.text}</span>
              {s.source && (
                <span className={V83_SOURCE} data-testid={`qa-party-${index}-${j}-source`}>{s.source}</span>
              )}
            </div>
          );
        })}
      </td>
      <td className={`${V83_TD} ${V83_TD_LABEL}`} data-label="Agreement" data-testid={`qa-agreement-${index}`}>
        {cell && (
          <>
            <span className={V83_BADGE[cell.tone]} data-testid={`qa-agreement-${index}-badge`}>{cell.text}</span>
            <span className={V83_SOURCE} data-testid={`qa-agreement-${index}-detail`}>{cell.detail}</span>
          </>
        )}
      </td>
      <td className={V83_TD}>
        <button
          type="button"
          className={V83_TABLE_BUTTON}
          aria-expanded={open}
          onClick={onView}
          data-testid={`qa-view-${index}`}
        >
          View agreement
        </button>
      </td>
    </tr>
  );
}

/** The agreement of one QWP (v83 l. 2106-2113): its state, what this
 *  revision changed (the published sheet, struck where the contract says),
 *  each company with who answered, when, and why — plus what this page
 *  already carried: where the request goes (the nomination stays here until
 *  step 5 has it, QMP-FE-8f), the line of a contested QWP (QMP-FE-8a, D30)
 *  and the history of the package. */
function AgreementPanel({
  row, entry, index, revision, diff, previousPackages, names, mine, acts, nominate, openTip, projectId,
}) {
  const box = agreementBox(entry);
  const approvals = (entry && entry.approvals) || [];
  return (
    <section className={V83_PANEL} data-testid={`qa-detail-${index}`}>
      <h3 className={V83_H3} data-testid={`qa-detail-${index}-title`}>{`${row.name} · Revision ${revision}`}</h3>
      {box && (
        <div className={box.agreed ? V83_SUCCESS : V83_PENDING} data-testid={`qa-gate-${index}`}>
          <strong className="font-medium" data-testid={`qa-gate-${index}-head`}>{box.head}</strong>
          <br />
          <span data-testid={`qa-gate-${index}-body`}>{box.body}</span>
        </div>
      )}
      <RulesBlock
        row={row}
        entry={entry}
        revision={revision}
        diff={diff}
        previousPackages={previousPackages}
        names={names}
        index={index}
      />
      <WhoApproved
        entry={entry}
        index={index}
        mine={mine}
        openTip={openTip}
        nominate={nominate ? (r, j) => nominate(r, j, index) : null}
      >
        {acts ? (r, j, isMine) => acts(r, j, isMine, index) : null}
      </WhoApproved>
      <ApprovalHistory
        projectId={projectId}
        qwpId={row.qwp_id}
        index={index}
        refreshKey={historyKey(approvals)}
      />
      <p className={`${V83_SMALL} mt-[12px]`} data-testid={`qa-detail-${index}-note`}>{APPROVALS_COPY.note}</p>
    </section>
  );
}

/** QMP-FE-8a (D30, decision 140 A) — "Your company’s next action" and
 *  "<company> · your review" (mockup v83 `approvalPage`, l. 2114-2130): the
 *  packages waiting for this company's approval, as the server served them
 *  (`approval_act.pending`), read and approved in ONE act; an objection stays
 *  one QWP at a time, with its own reason, through the door of one package
 *  (140 A: never one objection over several packages). The refusal of the act
 *  is NOT drawn here: it lives outside this panel, so it stays on screen when
 *  the panel goes (decision 6 of the addendum). */
function CompanyAct({
  rows, company, open, onOpen, ack, locked, canSign, onApprove,
  error, acting, objecting, formKeyOf, reasonOf, onObjecting, onReason, onObject, renderRules,
}) {
  const n = rows.length;
  const names = rows.map((r) => r.name).join(', ');
  if (!open) {
    return (
      <section className={V83_PANEL} data-testid="qa-next">
        <h3 className={V83_H3}>Your company’s next action</h3>
        <p className={V83_P} data-testid="qa-next-line">
          {`${company} has ${plural(n, 'QWP', 'QWPs')} to review: ${names}.`}
        </p>
        <button type="button" className={V83_PRIMARY} onClick={onOpen} data-testid="qa-act-review">
          Review my pending approvals
        </button>
      </section>
    );
  }
  return (
    <section className={V83_PANEL} data-testid="qa-own">
      <h3 className={V83_H3} data-testid="qa-own-title">{`${company} · your review`}</h3>
      <p className={V83_P} data-testid="qa-own-names">{names}</p>
      {rows.map((r) => {
        const fk = formKeyOf(r.qwp_id);
        const mineErr = error && acting === `own:${r.qwp_id}` && !error.reload ? error : null;
        const testId = `qa-own-${r.index}`;
        return (
          <div key={r.qwp_id} className="mt-[12px]" data-testid={testId}>
            {n > 1 && <strong className="font-medium" data-testid={`${testId}-name`}>{r.name}</strong>}
            {renderRules(r)}
            <button
              type="button"
              className={V83_QUIET}
              disabled={locked}
              onClick={() => onObjecting(objecting === fk ? null : fk)}
              data-testid={`${testId}-object`}
            >
              Object…
            </button>
            {objecting === fk && (
              <ObjectForm
                reason={reasonOf(fk)}
                onReason={(v) => onReason(fk, v)}
                onSend={() => onObject(r.qwp_id)}
                onCancel={() => onObjecting(null)}
                busy={locked}
                testId={testId}
              />
            )}
            <Refusal error={mineErr} place="act" testId={`${testId}-error`} />
            <Refusal error={mineErr} place="reason" testId={`${testId}-error-reason`} />
          </div>
        );
      })}
      <div className={V83_ACTIONS}>
        <button
          type="button"
          className={V83_PRIMARY}
          disabled={!ack || locked || !canSign}
          onClick={onApprove}
          data-testid="qa-act-approve"
        >
          {`Approve ${plural(n, 'QWP', 'QWPs')}`}
        </button>
      </div>
    </section>
  );
}

/** QMP-FE-8a — the act cannot be signed on this page (`actProblem`): the
 *  reason, and a way to read the plan again. Never "nothing pending". */
function ActUnavailable({ problem, locked, onReload }) {
  return (
    <section className={V83_PANEL} data-testid="qa-act-unavailable">
      <h3 className={V83_H3}>Your company’s next action</h3>
      <p className={V83_P} data-testid="qa-act-unavailable-line">{ACT_PROBLEM[problem]}</p>
      <button
        type="button"
        className={V83_QUIET}
        disabled={locked}
        onClick={onReload}
        data-testid="qa-act-unavailable-reload"
      >
        Reload
      </button>
    </section>
  );
}

export default function ApprovalsStep({
  editor, qmp, onGo, projectId,
}) {
  const { open, pop } = useTip(TIPS7);
  const published = isPublished(qmp);
  const summary = qmp.approvals_summary || {};
  const outstanding = Number(summary.outstanding || 0);
  const revision = Number(summary.revision || 0);
  const names = companyNames(editor);
  const structure = editor.ctx.structure || [];
  const packages = editor.ctx.packages || {};
  const [openRow, setOpenRow] = React.useState(-1);
  const { diff, previousPackages, error: diffError } = useQmpRevisionDiff(
    projectId, published ? revision : 0,
  );
  const settledAt = lastApprovalAt(packages);
  // ADDRESSING, not authority: which row is this reader's company.
  const { myParticipationId, membersOf } = useProjectMembership(projectId);
  const [nominating, setNominating] = React.useState(null);
  const [nomineeError, setNomineeError] = React.useState({});
  const statement = qmp.signing_statement;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // `acting` names WHAT was attempted, so a refusal lands where it was asked
  // for: `pkg:<qwp_id>` for a package answered on its own line (a contested
  // one), `own:<qwp_id>` for an objection inside "your review", `act` for the
  // one act of the company (QMP-FE-8a, D30). Never a second copy of who may
  // do it.
  const [acting, setActing] = React.useState(null);
  // The objection being written, and every reason typed, keyed by the
  // decision's context and the package (`formKey`) — never by a row index.
  const [objecting, setObjecting] = React.useState(null);
  const [reasons, setReasons] = React.useState({});
  const [ownReview, setOwnReview] = React.useState(false);
  const [feedback, setFeedback] = React.useState('');
  // `{kind: 'recorded' | 'unknown', decision}` — written but not re-read, or
  // no answer at all. Every decision stays locked until a reload lands.
  const [outcome, setOutcome] = React.useState(null);

  // QMP-FE-8a (D30) — the one act of the reader's company, AS SERVED, and why
  // it cannot be signed here when it cannot (nothing is recomputed: the sheet
  // shown and the scope sent are the same `GET /qmp`, the act's revision).
  const act = readAct(qmp.approval_act);
  const problem = actProblem(act, structure, packages);
  const offered = actOpen(act);
  const signAct = offered && !problem;
  const pendingRows = signAct ? actRows(structure, act).map((r) => ({
    ...r, index: structure.findIndex((s) => s.qwp_id === r.qwp_id),
  })) : [];
  // A row this reader may answer that is still WAITING (not contested): with
  // no act served for it, the page says the act is unavailable — never that
  // nothing is pending. It decides what is SAID, never what is signed.
  const waitingRow = structure.some((r) => {
    const d = decidableRow(packages[r.qwp_id]);
    return Boolean(d) && d.status !== 'objected';
  });
  const showUnavailable = published && Boolean(problem)
    && (offered || ((problem === 'missing' || problem === 'malformed') && waitingRow));
  // The company the act is for: the name the server serves on its rows.
  const actCompany = (() => {
    if (!act) return '';
    const served = structure.map((r) => ((packages[r.qwp_id] || {}).approvals || [])
      .find((a) => a.participation_id === act.participationId)).find(Boolean);
    return (served && served.name) || names[act.participationId] || '';
  })();
  // Is there anything on this plan THIS reader may still answer? The act the
  // server offers, or a line whose `can_decide` is the server's. No row -> no
  // signature box.
  const owesSomething = signAct
    || structure.some((r) => Boolean(decidableRow(packages[r.qwp_id])));

  // THE CONTEXT of every decision: project, company, revision. A result from
  // another context is not applied; a change of context clears the tick and
  // every objection being written.
  const participationId = (act && act.participationId) || myParticipationId || null;
  const context = decisionContext({ projectId, participationId, revision });
  const contextRef = React.useRef(context);
  contextRef.current = context;
  const formKey = (qwpId) => `${context}#${qwpId}`;
  React.useEffect(() => {
    setObjecting(null);
    setReasons({});
  }, [context]);

  // "BEFORE YOU SIGN" IS BOUND TO WHAT WAS READ: the context, the act's scope
  // as served, the statement's version. Another key is another reading — the
  // tick given for the old one does not count, and is cleared.
  const signKey = signingKey({
    projectId,
    participationId,
    revision,
    act: signAct ? act : null,
    statementVersion: statement ? statement.version : null,
  });
  const [ackKey, setAckKey] = React.useState(null);
  const ack = ackKey !== null && ackKey === signKey;
  const setAck = (on) => setAckKey(on ? signKey : null);
  React.useEffect(() => { setAckKey(null); }, [signKey]);
  React.useEffect(() => { if (!signAct) setOwnReview(false); }, [signAct]);

  // ONE ACTION AT A TIME: while a decision is sent or the plan re-read, and
  // while an outcome is not known, nothing else can be decided.
  const locked = busy || Boolean(qmp.loading) || Boolean(outcome);

  // SIGN. The body names the revision on screen, so an act against a plan that
  // moved is refused (409) instead of silently landing on the new one. 201 ->
  // read the plan again; NEVER a local optimism about what was recorded, and
  // never a second attempt on our own.
  const send = async (key, decision, call, done) => {
    if (locked) return;
    const started = contextRef.current;
    setBusy(true);
    setActing(key);
    setError(null);
    setFeedback('');
    let wrote = false;
    let unknown = false;
    try {
      await call();
      wrote = true;
    } catch (err) {
      if (contextRef.current === started) {
        if (outcomeUnknown(err)) {
          unknown = true;
          setAckKey(null);
        } else {
          const described = describeApprovalError(err);
          setError(described);
          // The plan or the scope moved under the page: what was ticked was
          // another reading.
          if (described.reload) setAckKey(null);
        }
      }
    }
    const fresh = contextRef.current === started;
    if (wrote && fresh) {
      setObjecting(null);
      setReasons({});
      setAckKey(null);
      setActing(null);
    }
    const read = await qmp.reload();
    if (fresh) {
      if (unknown) setOutcome({ kind: 'unknown', decision });
      else if (wrote && read === false) setOutcome({ kind: 'recorded', decision });
      else if (wrote && done) done();
    }
    setBusy(false);
  };

  // Read the plan again on request; only a read that landed unlocks.
  const reloadPlan = async () => {
    if (busy) return;
    setBusy(true);
    const read = await qmp.reload();
    if (read === true) setOutcome(null);
    setBusy(false);
  };

  const decide = (qwpId, decision, key, done) => send(key, decision, () => approveQmpPackage(
    projectId, qwpId, decisionBody({
      revision,
      decision,
      reason: reasons[formKey(qwpId)] || '',
      guideVersion: statement && statement.version,
    }),
  ), done);

  // THE ONE ACT (D30): every package pending for the company, as served.
  const approveAct = () => {
    if (!signAct || !statement || !ack) return;
    const n = act.pending.length;
    const company = actCompany;
    send('act', 'approved', () => approveQmpPackagesBatch(
      projectId, actBody(act, { guideVersion: statement.version }),
    ), () => {
      setOwnReview(false);
      setFeedback(`Approval by ${company} recorded on ${plural(n, 'QWP', 'QWPs')}. The other agreements are unchanged.`);
    });
  };
  const objectOne = (qwpId) => {
    const company = actCompany;
    decide(qwpId, 'objected', `own:${qwpId}`, () => {
      setFeedback(`Objection by ${company} recorded on 1 QWP. The other agreements are unchanged.`);
    });
  };
  const toggleForm = (fk) => {
    setObjecting(fk);
    if (fk) setReasons((prev) => ({ ...prev, [fk]: '' }));
  };
  const typeReason = (fk, value) => setReasons((prev) => ({ ...prev, [fk]: value }));

  // WHO MAY SAY WHERE THE REQUEST GOES (contract): the governing company
  // proposes for any participation, and a company corrects its own. The
  // governing participation is the project's own (`project_owner_auto`), the
  // same fact step 6 reads. The door decides in the end — 403
  // NOT_YOUR_PARTICIPATION, and the role fence above it — and its refusal is
  // shown on the line.
  const governing = (editor.companies || []).find((c) => c && c.source === 'project_owner_auto');
  const governingId = (governing && governing.id) || null;
  const mayAddress = (participationId) => Boolean(myParticipationId) && (
    myParticipationId === governingId || myParticipationId === participationId
  );

  const nominate = async (qwpId, participationId, userId) => {
    const key = `${qwpId}|${participationId}`;
    setNominating(key);
    setNomineeError((prev) => ({ ...prev, [key]: null }));
    try {
      await setQmpApprovalNominee(projectId, qwpId, participationId, userId);
    } catch (err) {
      setNomineeError((prev) => ({
        ...prev,
        [key]: describeApprovalError(err, 'Could not set who should be asked').message,
      }));
    }
    await qmp.reload();
    setNominating(null);
  };

  // The nomination cell of one company's line, when this reader may address it.
  const rowNominate = (approvalRow, j, index) => {
    const qwpId = structure[index].qwp_id;
    const participationId = approvalRow.participation_id;
    if (!mayAddress(participationId)) {
      return <NomineeLine row={approvalRow} index={index} position={j} />;
    }
    const key = `${qwpId}|${participationId}`;
    const testId = `qa-nominate-${index}-${j}`;
    return (
      <>
        <NomineeSelect
          row={approvalRow}
          people={membersOf(participationId)}
          busy={nominating === key}
          onPick={(userId) => (userId ? nominate(qwpId, participationId, userId) : null)}
          testId={testId}
        />
        <NomineeLine row={approvalRow} index={index} position={j} />
        <span className="basis-full text-[11.5px] text-[#5b6b85]" data-testid={`${testId}-note`}>
          Naming somebody sends them the request. It does not give them approval authority.
        </span>
        {nomineeError[key] && (
          <span className="basis-full" data-testid={`${testId}-error`}>
            <span className={ERR}>{nomineeError[key]}</span>
          </span>
        )}
      </>
    );
  };

  // The two acts of a line. THE SCREEN DOES NOT DECIDE AUTHORITY: they are
  // offered where the server said `can_decide` and the company has something
  // left to answer, and the door still answers every act. On this reader's own
  // company's line without `can_decide`: the sentence, and no button.
  // QMP-FE-8a (D30) — a package WAITING for the company is approved in the one
  // act below the table, so its line carries no controls; only a package the
  // company CONTESTS (an objection that still applies — not in the act) is
  // answered on its own line, through the door of one package.
  const rowActs = (approvalRow, j, isMine, index) => {
    if (!canDecide(approvalRow)) {
      return isMine ? (
        <span
          className="basis-full text-[11.5px] text-[#5b6b85]"
          data-testid={`qa-act-${index}-${j}-locked`}
        >
          Only a Quality Management Plan Signatory can approve.
        </span>
      ) : null;
    }
    if (isSettled(approvalRow)) return null;
    if (approvalRow.status !== 'objected') return null;
    const qwpId = structure[index].qwp_id;
    const key = `pkg:${qwpId}`;
    const fk = formKey(qwpId);
    const mineErr = error && acting === key ? error : null;
    const testId = `qa-act-${index}-${j}`;
    return (
      <>
        <span className="ml-auto flex gap-[8px]">
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={!ack || locked}
            onClick={() => decide(qwpId, 'approved', key)}
            data-testid={`${testId}-approve`}
          >
            Approve for your company
          </button>
          <button
            type="button"
            className={BTN_GHOST}
            disabled={locked}
            onClick={() => toggleForm(objecting === fk ? null : fk)}
            data-testid={`${testId}-object`}
          >
            Object…
          </button>
        </span>
        {objecting === fk && (
          <ObjectForm
            reason={reasons[fk] || ''}
            onReason={(v) => typeReason(fk, v)}
            onSend={() => decide(qwpId, 'objected', key)}
            onCancel={() => toggleForm(null)}
            busy={locked}
            testId={testId}
          />
        )}
        <span className="basis-full">
          <Refusal error={mineErr} place="act" testId={`${testId}-error`} />
          <Refusal error={mineErr} place="reason" testId={`${testId}-error-reason`} />
        </span>
      </>
    );
  };

  // The refusal of the act — and of an objection inside "your review" that
  // asks for the page to be read again — OUTSIDE the act's panel: when the
  // scope shrank, emptied, moved to a new revision or lost its authority, the
  // panel changes or goes, and the reason must not go with it.
  const actError = error && (acting === 'act'
    || (typeof acting === 'string' && acting.startsWith('own:') && error.reload)) ? error : null;

  return (
    <div className={CARD} data-testid="qs-pane-7">
      <LoadError editor={editor} />
      {feedback && (
        <div className={V83_FEEDBACK} role="status" data-testid="qa-feedback">{feedback}</div>
      )}

      {!published ? <NothingPublished onGo={onGo} /> : (
        <>
          <h2 className={V83_H2} data-testid="qa-title">{pageTitle(revision)}</h2>
          <p className={V83_INTRO} data-testid="qa-help">
            {`${APPROVALS_COPY.intro}${changedSentence(diff, structure, revision)}`}
          </p>
          <div className={V83_STATUS} data-testid="qa-statusline">
            <span data-testid="qa-due">{dueLine(qmp.approval_due, qmp.approvals_due_at)}</span>
            <span data-testid="qa-asked">{askedLine(companiesAsked(packages))}</span>
          </div>

          {/* D14.9, D26 — the SAME box step 6 signs with, the SAME served statement. */}
          {owesSomething && (
            <BeforeYouSign
              statement={statement}
              hint={statement && statement.hint_approve}
              ack={ack}
              onAck={setAck}
            >
              <Refusal error={error} place="ack" testId="qa-error-ack" />
            </BeforeYouSign>
          )}

          {diffError && (
            <div className="text-[12px] text-[#ffd28b] mb-[10px]" data-testid="qa-diff-error">
              {diffError}
            </div>
          )}
          <section className={V83_PANEL}>
            <table className={V83_TABLE} data-testid="qa-table">
              <thead className="max-[580px]:hidden">
                <tr>
                  <th className={V83_TH} style={{ width: '22%' }}>Quality Work Package</th>
                  <th className={V83_TH} style={{ width: '36%' }}>Approvals needed</th>
                  <th className={V83_TH}>Agreement</th>
                  <th className={V83_TH} style={{ width: '118px' }}>Details</th>
                </tr>
              </thead>
              <tbody className="max-[580px]:block">
                {structure.map((row, i) => (
                  <PackageRow
                    key={row.qwp_id}
                    row={row}
                    entry={packages[row.qwp_id]}
                    index={i}
                    revision={revision}
                    diff={diff}
                    open={openRow === i}
                    onView={() => setOpenRow(openRow === i ? -1 : i)}
                  />
                ))}
              </tbody>
            </table>
          </section>
          <div className={V83_INFO} data-testid="qa-info">{APPROVALS_COPY.info}</div>
          {openRow >= 0 && structure[openRow] && (
            <AgreementPanel
              row={structure[openRow]}
              entry={packages[structure[openRow].qwp_id]}
              index={openRow}
              revision={revision}
              diff={diff}
              previousPackages={previousPackages}
              names={names}
              mine={myParticipationId}
              acts={rowActs}
              nominate={rowNominate}
              openTip={open}
              projectId={projectId}
            />
          )}
          {signAct && (
            <CompanyAct
              rows={pendingRows}
              company={actCompany}
              open={ownReview}
              onOpen={() => { setOwnReview(true); setError(null); }}
              ack={ack}
              locked={locked}
              canSign={Boolean(statement)}
              onApprove={approveAct}
              error={error}
              acting={acting}
              objecting={objecting}
              formKeyOf={formKey}
              reasonOf={(fk) => reasons[fk] || ''}
              onObjecting={toggleForm}
              onReason={typeReason}
              onObject={objectOne}
              renderRules={(r) => (
                <RulesBlock
                  row={structure[r.index]}
                  entry={packages[r.qwp_id]}
                  revision={revision}
                  diff={diff}
                  previousPackages={previousPackages}
                  names={names}
                  index={r.index}
                  idp="qa-own"
                />
              )}
            />
          )}
          {showUnavailable && (
            <ActUnavailable problem={problem} locked={busy} onReload={reloadPlan} />
          )}
          <Refusal error={actError} place="act" testId="qa-act-error" />
          {outcome && (
            <div className={ERR} role="alert" data-testid={`qa-act-${outcome.kind}`}>
              {ACT_OUTCOME[outcome.kind][outcome.decision]}
              {' '}
              <button
                type="button"
                className="underline font-bold disabled:opacity-50"
                disabled={busy}
                onClick={reloadPlan}
                data-testid="qa-act-reload"
              >
                Reload
              </button>
            </div>
          )}
        </>
      )}
      {published && (
        <div className={PREVIEW} data-testid="qa-record">
          <div className={`${BOX_T} mb-[8px]`}>The approval record</div>
          <ul>
            <li className={PREVIEW_LI} data-testid="qa-record-line">
              {outstanding === 0 ? (
                <>
                  <b>{recordHeadline(summary.revision, qmp.current_revision, settledAt)}</b>
                  {' No QA sign-offs are paused because of QMP approval.'}
                </>
              ) : (
                <>
                  <b>{`${outstanding} company approval${outstanding === 1 ? '' : 's'}`}</b>
                  {' still outstanding for this revision.'}
                </>
              )}
            </li>
            {/* The revisions this plan has had. It is what `history[]` serves
                and ALL it serves: when each revision was published and by
                whom. The decisions taken against earlier revisions are not on
                the wire, so nothing here claims them. */}
            {(qmp.history || []).map((h) => (
              <li className={PREVIEW_LI} key={h.revision} data-testid={`qa-history-${h.revision}`}>
                <b>{`Revision ${h.revision}`}</b>
                {` · published ${formatDay(h.declared_at, { withYear: true }) || '—'}`}
                {h.declared_by_name ? ` by ${h.declared_by_name}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {pop}
    </div>
  );
}
